import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ResponsiveContainer, ResponsiveContent, ResponsiveHeader, ResponsiveFooter } from '../ui/ResponsiveLayout';
import { useSessionPersistence } from '../../hooks/useSessionPersistence';
import type { UnifiedContact } from '../../services/unifiedContactService';
import { unifiedContactService } from '../../services/unifiedContactService';
import ManualContactModal from '../ui/ManualContactModal';

interface SelectedExecutor {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

function sameExecutors(a: SelectedExecutor[], b: SelectedExecutor[]) {
  if (a.length !== b.length) return false;
  const aIds = a.map((x) => x.id).sort();
  const bIds = b.map((x) => x.id).sort();
  for (let i = 0; i < aIds.length; i++) {
    if (aIds[i] !== bIds[i]) return false;
  }
  return true;
}

const Frame261: React.FC = () => {
  const navigate = useNavigate();
  const { session, updateTaskProgress } = useSessionPersistence();

  const [contacts, setContacts] = useState<UnifiedContact[]>([]);
  const [selectedExecutors, setSelectedExecutors] = useState<SelectedExecutor[]>([]);
  const [executorNote, setExecutorNote] = useState<string>('');
  const [showManualModal, setShowManualModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // New state for lifecycle management
  const [editingContact, setEditingContact] = useState<UnifiedContact | null>(null);

  const savedExecutorsFromSession: SelectedExecutor[] = useMemo(() => {
    return session.taskProgress?.executors?.answers?.selectedExecutors ?? [];
  }, [session.taskProgress?.executors?.answers?.selectedExecutors]);

  const savedNoteFromSession: string = useMemo(() => {
    return session.taskProgress?.executors?.answers?.executorNote ?? '';
  }, [session.taskProgress?.executors?.answers?.executorNote]);

  useEffect(() => {
    let cancelled = false;
    const loadContacts = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const allContacts = await unifiedContactService.getAllContacts();
        if (cancelled) return;
        setContacts(allContacts);
      } catch (err) {
        if (cancelled) return;
        console.error('[Frame261] Error loading contacts:', err);
        setError('Failed to load contacts');
      } finally {
        if (!cancelled) setIsLoading(false);
      }};
    void loadContacts();
    return () => {
      cancelled = true;
    };
  }, [session.id]);

  useEffect(() => {
    setSelectedExecutors((prev) => (sameExecutors(prev, savedExecutorsFromSession) ? prev : savedExecutorsFromSession));
    setExecutorNote((prev) => (prev === savedNoteFromSession ? prev : savedNoteFromSession));
  }, [savedExecutorsFromSession, savedNoteFromSession]);

  const toggleExecutor = (contact: UnifiedContact) => {
    setSelectedExecutors((prev) => {
      const isSelected = prev.some((e) => e.id === contact.id);
      if (isSelected) return prev.filter((e) => e.id !== contact.id);
      return [
        ...prev,
        {
          id: contact.id,
          firstName: contact.firstName,
          lastName: contact.lastName,
          email: contact.email || '',
        },
      ];
    });
  };

  const didHydrateRef = useRef(false);
  const saveTimerRef = useRef<number | null>(null);

  const saveNow = async (nextExecutors: SelectedExecutor[], nextNote: string) => {
    const hasExecutors = nextExecutors.length > 0;
    const hasNote = nextNote.trim().length > 0;
    const hasMeaningful = hasExecutors || hasNote;
    if (!hasMeaningful) {
      await updateTaskProgress('executors', { selectedExecutors: [], executorNote: '' }, 'not-started');
      return;
    }
    await updateTaskProgress(
      'executors',
      { selectedExecutors: nextExecutors, executorNote: nextNote },
      'in-progress'
    );
  };

  useEffect(() => {
    if (!didHydrateRef.current) {
      didHydrateRef.current = true;
      return;
    }
    if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
    saveTimerRef.current = window.setTimeout(() => {
      void saveNow(selectedExecutors, executorNote).catch((e) => {
        console.warn('[Frame261] autosave failed', e);
      });
    }, 350);
    return () => {
      if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
    };
  }, [selectedExecutors, executorNote]);

  const handleEditContact = (e: React.MouseEvent, contact: UnifiedContact) => {
    e.stopPropagation();
    setEditingContact(contact); 
    setShowManualModal(true);
  };

  const handleDeleteContact = async (e: React.MouseEvent, contactId: string) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this contact? This will also remove them from your selected executors.')) return;

    try {
      await unifiedContactService.deleteContact(contactId);
      const allContacts = await unifiedContactService.getAllContacts();
      setContacts(allContacts);
      setSelectedExecutors(prev => prev.filter(ex => ex.id !== contactId));
    } catch (err) {
      console.error('Failed to delete contact:', err);
      alert('Failed to delete contact.');
    }
  };

  const handleManualContactSubmit = async (contactData: unknown) => {
    try {
      if (editingContact) {
        await unifiedContactService.updateContact(editingContact.id, {
          firstName: contactData.firstName.trim(),
          lastName: contactData.lastName.trim(),
          email: contactData.email?.trim() || '',
          phone: contactData.phone?.trim(),
          notes: contactData.notes?.trim(),
        });
      } else {
        const newContact = await unifiedContactService.createContact({
          firstName: contactData.firstName.trim(),
          lastName: contactData.lastName.trim(),
          email: contactData.email?.trim() || '',
          phone: contactData.phone?.trim(),
          relationship: 'executor',
          source: 'manual',
          notes: contactData.notes?.trim() || '', 
        });

        if (newContact) {
          setSelectedExecutors((prev) => [
            ...prev,
            {
              id: newContact.id,
              firstName: contactData.firstName.trim(),
              lastName: contactData.lastName.trim(),
              email: (contactData.email || '').trim(),
            },
          ]);
        }
      }

      const allContacts = await unifiedContactService.getAllContacts();
      setContacts(allContacts);
      setShowManualModal(false);
      setEditingContact(null);
    } catch (err: unknown) {
      if (err?.code === 'DUPLICATE_CONTACT') {
        alert(`A contact with email ${contactData.email} already exists.`);
      } else {
        alert('Failed to process contact. Please try again.');
      }
      throw err;
    }
  };

  const handleNext = async () => {
    try {
      await saveNow(selectedExecutors, executorNote);
      navigate('/frame-215');
    } catch (err: unknown) {
      console.error('[Frame261] Error saving executors:', err);
      setError('Failed to save executor selection. Please try again.');
    }
  };

  const handleBack = async () => {
    try {
      await saveNow(selectedExecutors, executorNote);
    } catch (e) {
      console.warn('[Frame261] back-save failed', e);
    }
    navigate('/frame-214');
  };

  const getPersonName = (contact: UnifiedContact) => `${contact.firstName} ${contact.lastName}`.trim();

  return (
    <ResponsiveContainer>
      <ResponsiveContent>
        <ResponsiveHeader title="Choose Your Executor" />

        <div className="max-w-4xl space-y-6">
          <div className="space-y-4">
            <p className="text-stone-800 text-lg font-medium font-['Work_Sans'] leading-relaxed">
              Select one or more people to serve as your executor(s). You’ll be able to invite them to access this plan later.
            </p>
            <p className="text-stone-600 text-sm">
              An executor is responsible for managing your estate and carrying out your wishes after you pass.
              Choose someone you trust who is organized and capable of handling financial and legal matters.
              You can change or update executor access at any time.
            </p>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 text-xl">⚖️</div>
              <div>
                <h3 className="font-semibold text-yellow-900 mb-1">Important Note</h3>
                <p className="text-sm text-yellow-800">
                  Adding an executor in End of an Era helps you record your intentions, but it does not create
                  a legally binding appointment. Executor designations are only legally recognized when named in
                  a valid will or trust. For legal validity, work with an attorney to create or update your estate
                  planning documents to formally appoint this person as your executor.
                </p>
              </div>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
              {error}
            </div>
          )}

          {isLoading ? (
            <div className="text-center py-12">
              <div className="inline-block w-8 h-8 border-4 border-cyan-700 border-t-transparent rounded-full animate-spin" />
              <p className="text-stone-600 mt-4">Loading contacts...</p>
            </div>
          ) : (
            <div className="space-y-4">
              {selectedExecutors.length > 0 && (
                <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4">
                  <h3 className="font-semibold text-cyan-900 mb-2">
                    Selected Executor{selectedExecutors.length > 1 ? 's' : ''} ({selectedExecutors.length})
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedExecutors.map((executor) => (
                      <span
                        key={executor.id}
                        className="inline-flex items-center gap-2 px-3 py-1 bg-white rounded-full text-sm text-cyan-800 border border-cyan-300"
                      >
                        {executor.firstName} {executor.lastName}
                        <button
                          type="button"
                          onClick={() => setSelectedExecutors((prev) => prev.filter((e) => e.id !== executor.id))}
                          className="text-cyan-600 hover:text-cyan-800"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="bg-white rounded-lg border border-stone-200">
                <div className="p-4 border-b border-stone-200 flex justify-between items-center">
                  <h3 className="font-semibold text-stone-800">Your Contacts</h3>
                  <button
                    type="button"
                    onClick={() => {
                      setEditingContact(null);
                      setShowManualModal(true);
                    }}
                    className="text-sm text-cyan-700 hover:text-cyan-800 font-medium"
                  >
                    + Add New Contact
                  </button>
                </div>

                {contacts.length === 0 ? (
                  <div className="p-8 text-center text-stone-500">
                    <p>No contacts found.</p>
                    <button
                      type="button"
                      onClick={() => {
                        setEditingContact(null);
                        setShowManualModal(true);
                      }}
                      className="mt-2 text-cyan-700 hover:text-cyan-800 font-medium"
                    >
                      Add your first contact
                    </button>
                  </div>
                ) : (
                  <div className="max-h-96 overflow-y-auto">
                    {contacts.map((contact) => {
                      const isSelected = selectedExecutors.some((e) => e.id === contact.id);

                      return (
                        <div
                          key={contact.id}
                          onClick={() => toggleExecutor(contact)}
                          className={`p-4 border-b border-stone-100 last:border-b-0 cursor-pointer transition-colors flex justify-between items-center ${
                            isSelected ? 'bg-cyan-50' : 'hover:bg-stone-50'
                          }`}
                        >
                          <div className="flex items-center gap-4 flex-1 min-w-0">
                            <div
                              className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                                isSelected ? 'bg-cyan-700 border-cyan-700' : 'border-stone-300'
                              }`}
                            >
                              {isSelected && (
                                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </div>

                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-stone-800 truncate">{getPersonName(contact)}</div>
                              <div className="text-sm text-stone-600 truncate">
                                {contact.email || <span className="text-orange-600">No email address</span>}
                              </div>
                            </div>
                          </div>

                          <div className="flex gap-2 ml-4">
                            <button
                              type="button"
                              onClick={(e) => handleEditContact(e, contact)}
                              className="text-cyan-600 hover:text-cyan-700 px-3 py-1 rounded border border-cyan-200 hover:border-cyan-300 text-s font-medium bg-white"
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={(e) => handleDeleteContact(e, contact.id)}
                              className="text-red-600 hover:text-red-700 px-3 py-1 rounded border border-red-200 hover:border-red-300 text-s font-medium bg-white"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="bg-white border border-stone-200 rounded-lg p-4">
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Personal Message (Optional)
                </label>
                <textarea
                  value={executorNote}
                  onChange={(e) => setExecutorNote(e.target.value)}
                  placeholder="Add a personal note that will be included in the invitation email to your executor(s)..."
                  rows={4}
                  className="w-full px-3 py-2 border border-stone-300 rounded-md text-sm text-stone-700 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                />
                <p className="mt-1 text-xs text-stone-500">
                  This message will be included in the email invitation sent to all selected executors.
                </p>
              </div>

              <p className="text-sm text-stone-500 text-center">
                You can skip this step and add an executor later from your dashboard.
              </p>
            </div>
          )}
        </div>
      </ResponsiveContent>

      <ResponsiveFooter
        title="Complete Your Legal Essentials"
        subtitle="Choose someone to carry out your wishes"
        progressPercent={66}
        onNext={handleNext}
        onBack={handleBack}
        nextText="Next"
        backText="Back"
      />

      <ManualContactModal
        open={showManualModal}
        onClose={() => {
          setShowManualModal(false);
          setEditingContact(null);
        }}
        onSubmit={handleManualContactSubmit}
        initialData={editingContact ?? undefined} 
        loading={isLoading}
      />
    </ResponsiveContainer>
  );
};

export default Frame261;